import { useEffect, useState } from 'react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { fetchStats, type AdminStats } from '@/lib/adapters'
import { ErrorState, Skeleton } from '@/components/States'

/**
 * A2.01 — загальна панель. Тільки реальні лічильники з `/admin/stats`
 * (users/channels/bots — прямий count по існуючих MongoDB-колекціях).
 * DAU/MAU/funnel/revenue/messages-per-day прибрані — потребують ClickHouse/
 * Reverb-подібної аналітичної інфраструктури, якої на сервері немає.
 */

function KPICard({
  label,
  value,
  loading,
}: {
  label: string
  value: number | undefined
  loading: boolean
}) {
  return (
    <div className="rounded-lg border border-border-default bg-surface-2 p-3">
      {loading ? (
        <Skeleton className="h-8 w-24" />
      ) : (
        <p className="font-mono text-2xl font-semibold text-primary tabular">
          {value?.toLocaleString() ?? '—'}
        </p>
      )}
      <p className="mt-1 text-sm text-secondary">{label}</p>
    </div>
  )
}

export function DashboardScreen() {
  const t = useT()
  const [stats, setStats] = useState<AdminStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)

    fetchStats(ac.signal)
      .then((s) => {
        if (alive) setStats(s)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })

    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])

  return (
    <>
      <PageHeader title={t.nav.dashboard} screenId="A2.01" verified />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {error ? (
          <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <KPICard label={t.dash.users} value={stats?.users} loading={loading} />
            <KPICard label={t.nav.groups} value={stats?.channels} loading={loading} />
            <KPICard label={t.nav.bots} value={stats?.bots} loading={loading} />
          </div>
        )}
      </div>
    </>
  )
}
